import { useEffect, useState, lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  useLocation,
  useNavigation,
} from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import CookieConsent from 'react-cookie-consent';

// Critical components - loaded immediately (core user journey)
import Layout from './components/Layout';
import Landing from './pages/Landing';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import Footer from './components/Footer';
import EmbeddedBrowserWarning from './components/EmbeddedBrowserWarning';
import Dashboard from './pages/Dashboard';
import TrackDetail from './pages/TrackDetail';
import CreateTrack from './pages/CreateTrack';
import TrackCollaborators from './pages/TrackCollaborators';
import AudioCommentsPage from './pages/AudioCommentsPage';
import AudioCommentsLegacyRedirect from './pages/AudioCommentsLegacyRedirect';
import GDriveCallback from './pages/GDriveCallback';
import LoginPage from './pages/LoginPage';
import LinkStoragePage from './pages/LinkStoragePage';
import StorageCallback from './pages/StorageCallback';
import WelcomeOnboardingPage from './pages/WelcomeOnboardingPage';

// Public SEO/marketing pages — eager (stable main.js must not depend on hashed lazy chunks)
import Help from './pages/Help';
import DocsHubPage from './pages/DocsHubPage';
import McpDocsPage from './pages/McpDocsPage';
import PublicPricingPage from './pages/PublicPricingPage';
import GuidePage from './pages/GuidePage';

// Lazy-loaded page components (less frequently accessed)
const Settings = lazy(() => import('./pages/Settings'));
const TransferStoragePage = lazy(() => import('./pages/TransferStoragePage'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./pages/PaymentCancel'));
const ComingSoon = lazy(() => import('./pages/ComingSoon'));
const AccessDenied = lazy(() => import('./pages/AccessDenied'));
const UserProfileInfo = lazy(() => import('./pages/UserProfileInfo'));

import ga, { hasAnalyticsConsent } from './services/analytics';
import { captureUtmFromLocationToLocalStorage } from './utils/utm';
import { relayMetaCapiEvent, shouldSendMetaLandingPageView, createMetaEventId } from './services/metaCapiRelay';
import CassetteLoader from './components/CassetteLoader';
import RouteErrorFallback from './components/RouteErrorFallback';
import AnonymousAuthModals from './components/AnonymousAuthModals';
import {
  rootIndexLoader,
  protectedLoader,
  guestLoader,
  storageLoader,
  dashboardLoader,
} from './auth/routeGuards';

/** Tracks GA page views on route change. */
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    const eventId = createMetaEventId('PageView', location.pathname);
    ga.pageView(location.pathname, document.title, eventId);
  }, [location.pathname]);
  return null;
}

/** Full-screen single loader used for boot and slow transitions. */
function BootLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] text-[var(--color-text)]">
      <CassetteLoader size={80} variant="overlay" />
    </div>
  );
}

/** Shows one consistent overlay during route transitions, after a short delay so fast nav doesn't flash. */
function NavigationPendingOverlay() {
  const navigation = useNavigation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (navigation.state === 'idle') {
      setShow(false);
      return;
    }
    const id = window.setTimeout(() => setShow(true), 150);
    return () => window.clearTimeout(id);
  }, [navigation.state]);

  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[900] flex items-center justify-center bg-[var(--color-bg)]/70 backdrop-blur-sm pointer-events-none">
      <CassetteLoader size={72} variant="overlay" />
    </div>
  );
}

function RootLayout() {
  return (
    <div className="App min-h-screen flex flex-col bg-[var(--color-bg)] text-[var(--color-text)]">
      <RouteTracker />
      <EmbeddedBrowserWarning />
      <Toaster position="top-right" />
      <PWAInstallPrompt />
      <NavigationPendingOverlay />
      <Outlet />
      <AnonymousAuthModals />
      <CookieConsent
        location="bottom"
        buttonText="Accept"
        declineButtonText="Decline"
        cookieName="cassette_gdpr_consent"
        style={{
          background: '#2B373B',
          fontSize: '0.875rem',
          padding: '16px',
          zIndex: 1000,
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
        }}
        buttonStyle={{
          background: '#4CAF50',
          color: 'white',
          fontSize: '0.875rem',
          padding: '8px 16px',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
        }}
        declineButtonStyle={{
          background: '#f44336',
          color: 'white',
          fontSize: '0.875rem',
          padding: '8px 16px',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          marginLeft: '10px',
        }}
        expires={365}
        onAccept={() => {
          ga.init(true);
          ga.pageView(window.location.pathname, document.title);
        }}
        onDecline={() => {
          ga.disable();
        }}
      >
        We use localStorage and sessionStorage for core app functionality. If you accept, we also enable
        analytics that may set first‑party cookies to measure usage (no advertising cookies). By accepting,
        you consent to this limited use to improve the product.{' '}
        <a
          href="/policy.html"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#4CAF50', textDecoration: 'underline' }}
        >
          Learn more in our Privacy Policy
        </a>
      </CookieConsent>
    </div>
  );
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorFallback />,
    hydrateFallbackElement: <BootLoader />,
    children: [
      // Landing page - completely independent, no footer
      { index: true, element: <Landing />, loader: rootIndexLoader },

      // Other public routes with footer
      { path: 'auth/gdrive/callback', element: <><GDriveCallback /><Footer /></> },
      { path: 'auth/storage/callback', element: <><StorageCallback /><Footer /></> },
      {
        path: 'payment/success',
        element: (
          <Suspense fallback={<CassetteLoader />}>
            <PaymentSuccess />
            <Footer />
          </Suspense>
        ),
      },
      {
        path: 'payment/cancel',
        element: (
          <Suspense fallback={<CassetteLoader />}>
            <PaymentCancel />
            <Footer />
          </Suspense>
        ),
      },

      // Protected routes - wrapped with Layout shell
      {
        element: <Layout />,
        loader: protectedLoader,
        errorElement: <RouteErrorFallback />,
        children: [
          { path: 'welcome', element: <WelcomeOnboardingPage /> },
          { path: 'dashboard', element: <Dashboard />, loader: dashboardLoader },
          { path: 'tracks/new', element: <CreateTrack /> },
          { path: 'tracks/:id', element: <TrackDetail /> },
          { path: 'tracks/:id/collaborators', element: <TrackCollaborators /> },
          { path: 'tracks/:trackId/audio/comments', element: <AudioCommentsPage /> },
          { path: 'tracks/:trackId/audio/:fileId/comments', element: <AudioCommentsPage /> },
          { path: 'tracks/:trackId/audio/*', element: <AudioCommentsLegacyRedirect /> },
          {
            path: 'settings',
            element: (
              <Suspense fallback={<CassetteLoader />}>
                <Settings />
              </Suspense>
            ),
          },
          {
            path: 'settings/transfer',
            element: (
              <Suspense fallback={<CassetteLoader />}>
                <TransferStoragePage />
              </Suspense>
            ),
          },
        ],
      },

      {
        path: 'coming-soon',
        element: (
          <Suspense fallback={<CassetteLoader />}>
            <ComingSoon />
            <Footer />
          </Suspense>
        ),
      },
      {
        path: 'access-denied',
        element: (
          <Suspense fallback={<CassetteLoader />}>
            <AccessDenied />
            <Footer />
          </Suspense>
        ),
      },
      {
        path: 'u/:userId',
        element: (
          <Suspense fallback={<CassetteLoader />}>
            <UserProfileInfo />
            <Footer />
          </Suspense>
        ),
      },
      {
        path: 'help',
        element: (
          <Suspense fallback={<CassetteLoader />}>
            <Help />
            <Footer />
          </Suspense>
        ),
      },
      {
        path: 'upgrade',
        element: (
          <Suspense fallback={<CassetteLoader />}>
            <PublicPricingPage />
            <Footer />
          </Suspense>
        ),
      },
      {
        path: 'docs',
        element: (
          <Suspense fallback={<CassetteLoader />}>
            <DocsHubPage />
            <Footer />
          </Suspense>
        ),
      },
      {
        path: 'docs/mcp',
        element: (
          <Suspense fallback={<CassetteLoader />}>
            <McpDocsPage mcpProduct="music" showCreatorTools={false} />
            <Footer />
          </Suspense>
        ),
      },
      {
        path: 'guides/:slug',
        element: (
          <Suspense fallback={<CassetteLoader />}>
            <GuidePage />
            <Footer />
          </Suspense>
        ),
      },
      { path: 'auth/login', element: <><LoginPage /><Footer /></>, loader: guestLoader },
      { path: 'auth/storage', element: <><LinkStoragePage /><Footer /></>, loader: storageLoader },
      { path: 'auth/gdrive', element: <><LinkStoragePage /><Footer /></> },
    ],
  },
]);

function App() {
  useEffect(() => {
    // Capture UTM params on first load (first-touch wins)
    captureUtmFromLocationToLocalStorage();

    const pageViewEventId = createMetaEventId('PageView', String(Date.now()));

    // Server-side Meta PageView for paid Meta landings (once per session, consent-free)
    if (shouldSendMetaLandingPageView()) {
      relayMetaCapiEvent('PageView', { eventId: pageViewEventId });
    }

    if (import.meta.env.VITE_ENV === 'LOCAL') {
      console.log('[Analytics] Running in local development - analytics disabled to prevent external script errors');
    }

    if (hasAnalyticsConsent()) {
      ga.init(true);
      ga.pageView(window.location.pathname, document.title, pageViewEventId);
    }
  }, []);

  return <RouterProvider router={router} />;
}

export default App;
